import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AppMessageSeverities } from '@/types';
import { Button, Dialog, DialogActions, DialogContent, Divider } from '@/ui';

import type { Company, CompanyForm } from '@/types';
import type { FC } from 'react';

import VisionAndGoals from '@/components/Company/Info/Edit/Goals';
import {
  getCompanyMutationErrorMessage,
  getCompanyMutationFieldErrors,
  useUpdateCompanyMutation,
} from '@/store/api/companyApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';

const PartnerEditCompanyDialog: FC<{
  company: Company;
  onClose: () => void;
}> = ({ company: currentCompany, onClose }) => {
  const dispatch = useAppDispatch();
  const [updateCompany, { isLoading: loading }] = useUpdateCompanyMutation();
  const [company, setCompany] = useState(currentCompany);

  const onCompanyChange = (updated: Company) => setCompany(updated);

  const defaultValues = {
    culture: '',
    vision: '',
  } satisfies Partial<CompanyForm>;

  const { control, handleSubmit, setError, reset } = useForm<CompanyForm>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    reset({
      culture: currentCompany.culture,
      vision: currentCompany.vision,
    });
  }, [currentCompany.culture, currentCompany.id, currentCompany.vision]);

  const onSave = () => {
    handleSubmit(async (data) => {
      try {
        const payload = {
          culture: data.culture,
          vision: data.vision,
          goals: company.goals.filter((goal) => !!goal.trim()),
        };

        await updateCompany({
          id: company.id,
          data: payload,
        }).unwrap();
        onClose();
      } catch (e) {
        const errors = getCompanyMutationFieldErrors(e);
        type FormKeys = keyof CompanyForm;
        const formFields = Object.keys(defaultValues);
        const hasFieldErrors = formFields.some((field) => errors[field]);

        formFields.forEach((field) => {
          const errorValues = errors[field];
          if (errorValues) {
            setError(field as FormKeys, {
              message: errorValues.join(','),
              type: 'custom',
            });
          }
        });

        if (!hasFieldErrors) {
          dispatch(
            setAppMessage({
              message: getCompanyMutationErrorMessage(e),
              open: true,
              severity: AppMessageSeverities.Error,
            }),
          );
        }
      }
    })();
  };

  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogContent>
        <VisionAndGoals
          company={company}
          control={control}
          onCompanyChange={onCompanyChange}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={loading} variant="contained" onClick={onSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerEditCompanyDialog;
